


import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Card, CardContent, CardHeader, Typography, Button, MenuItem,
  TextField, Tab, Tabs, Table, TableHead, TableBody, TableRow,
  TableCell, Chip, Avatar, CircularProgress, Alert, Grid,
  LinearProgress, IconButton, Tooltip,
} from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import { useLocation } from "react-router-dom";
import recruiterTheme, {
  FONT_BODY, FONT_DISPLAY, TYPE, RADIUS, SHADOW, PAGE_PADDING, COLORS as THEME, CONTROL_HEIGHT, ELLIPSIS,
} from "../../styles/recruiterTheme";
import {
  Dashboard, People, Business, TrendingUp, Source,
  Download, Refresh, WorkOutline, CheckCircle,
  AccessTime,
} from "@mui/icons-material";
import {
  Chart as ChartJS,
  CategoryScale, LinearScale, BarElement, LineElement,
  PointElement, ArcElement, Title, Tooltip as ChartTooltip,
  Legend, Filler,
} from "chart.js";
import { Bar, Doughnut, Line } from "react-chartjs-2";

ChartJS.register(
  CategoryScale, LinearScale, BarElement, LineElement,
  PointElement, ArcElement, Title, ChartTooltip, Legend, Filler
);

// ── API ───────────────────────────────────────────────────────────────────────
const BASE = process.env.REACT_APP_API_BASE_URL ;

// utils/apiInterceptor.js adds the Bearer token. (This used to send
// "Bearer <access_token>", a key nothing writes, which blocked the real token.)
const getHeaders = () => ({ "Content-Type": "application/json" });

const fetchReport = async (endpoint, period) => {
  const r = await fetch(`${BASE}/reports/${endpoint}?period=${encodeURIComponent(period)}`, { headers: getHeaders() });
  let body = {};
  try { body = await r.json(); } catch { /* non-JSON body */ }
  if (!r.ok || body?.success === false) {
    throw new Error(body?.message || `Could not load ${endpoint.replace(/-/g, " ")} (${r.status})`);
  }
  return body;
};

const arr = (v) => (Array.isArray(v) ? v : []);
const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
const initialsOf = (name) => String(name || "?").split(/\s+/).filter(Boolean).map(w => w[0]).join("").slice(0, 2).toUpperCase() || "?";

// CSV export of the active report tab (client-side; no extra endpoint).
const toCsv = (headers, rows) => {
  const esc = (v) => {
    const t = v == null ? "" : String(v);
    return /[",\n]/.test(t) ? `"${t.replace(/"/g, '""')}"` : t;
  };
  return [headers, ...rows].map(r => r.map(esc).join(",")).join("\n");
};
const downloadCsv = (filename, csv) => {
  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmt = (v) => {
  if (!v) return "—";
  if (v >= 10000000) return `₹${(v / 10000000).toFixed(1)}Cr`;
  if (v >= 100000)   return `₹${(v / 100000).toFixed(1)}L`;
  return new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(v);
};

// ── Chart defaults ────────────────────────────────────────────────────────────
const BAR_OPTS = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: { legend: { display: false } },
  scales: {
    y: { beginAtZero: true, grid: { color: "rgba(0,0,0,0.04)" }, ticks: { font: { size: TYPE.overline, family: FONT_BODY } } },
    x: { grid: { display: false }, ticks: { font: { size: TYPE.overline, family: FONT_BODY } } },
  },
};

const DONUT_OPTS = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { position: "right", labels: { padding: 16, usePointStyle: true, font: { size: TYPE.caption, family: FONT_BODY } } },
  },
  cutout: "62%",
};

// Chart series colours (unchanged).
const COLORS = ["#1a237e","#0277bd","#00838f","#2e7d32","#e65100","#6a1b9a","#c62828","#558b2f"];
const SOFT   = COLORS.map(c => c + "cc");

// ── Sub-components ────────────────────────────────────────────────────────────
// White "card on canvas".
const cardSx = {
  bgcolor: THEME.surface, border: `1px solid ${THEME.border}`, borderRadius: `${RADIUS.lg}px`,
  boxShadow: SHADOW.card, overflow: "hidden",
};
const cardHeaderSx = { px: 3, py: 2, borderBottom: `1px solid ${THEME.border}` };
const CardTitle = ({ children }) => (
  <Typography sx={{ fontFamily: FONT_BODY, fontWeight: 700, fontSize: TYPE.cardTitle, color: THEME.primaryDeep }}>{children}</Typography>
);
const headCellSx = {
  fontFamily: FONT_BODY, fontWeight: 700, fontSize: TYPE.overline, color: THEME.muted,
  textTransform: "uppercase", letterSpacing: "0.08em", whiteSpace: "nowrap",
};
const numCellSx = { fontSize: TYPE.small, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" };
const statSx = { fontFamily: FONT_BODY, fontWeight: 800, fontVariantNumeric: "tabular-nums", lineHeight: 1.1 };

const KpiCard = ({ title, value, sub, icon, color }) => (
  <Card elevation={0} sx={{ ...cardSx, borderRadius: `${RADIUS.md}px`, height: "100%" }}>
    <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={2}>
        <Box minWidth={0}>
          <Typography sx={{ fontSize: TYPE.overline, fontWeight: 700, color: THEME.muted, textTransform: "uppercase", letterSpacing: "0.08em" }}>{title}</Typography>
          <Typography sx={{ ...statSx, fontSize: TYPE.page, mt: 0.5, color }}>{value}</Typography>
          {sub && <Typography sx={{ fontSize: TYPE.caption, color: THEME.muted, mt: 0.5 }}>{sub}</Typography>}
        </Box>
        <Avatar sx={{ bgcolor: `${color}18`, color, width: 40, height: 40 }}>{icon}</Avatar>
      </Box>
    </CardContent>
  </Card>
);

const ChartCard = ({ title, height = 280, children }) => (
  <Card elevation={0} sx={cardSx}>
    <CardHeader title={<CardTitle>{title}</CardTitle>} sx={cardHeaderSx} />
    <CardContent sx={{ p: 3 }}>
      <Box height={height}>{children}</Box>
    </CardContent>
  </Card>
);

const EmptyCard = ({ icon, title, sub }) => (
  <Card elevation={0} sx={cardSx}>
    <Box display="flex" flexDirection="column" alignItems="center" py={8} gap={1}>
      {React.cloneElement(icon, { sx: { fontSize: 48, color: THEME.faint } })}
      <Typography sx={{ fontWeight: 700, fontSize: TYPE.body, color: THEME.ink }}>{title}</Typography>
      <Typography sx={{ fontSize: TYPE.small, color: THEME.muted }}>{sub}</Typography>
    </Box>
  </Card>
);

const TableCard = ({ title, children }) => (
  <Card elevation={0} sx={cardSx}>
    <CardHeader title={<CardTitle>{title}</CardTitle>} sx={cardHeaderSx} />
    <Box sx={{ overflowX: "auto" }}>{children}</Box>
  </Card>
);

// ── Fallback data (shown when API has no real data yet) ───────────────────────
const FALLBACK = {
  overview: {
    total_jobs: 0, open_jobs: 0, total_candidates: 0, total_placements: 0,
    fill_rate: 0, avg_time_to_fill: 0, revenue: 0,
    job_status_counts: {}, candidate_counts: {},
  },
  funnel: [
    { stage: "Sourced",     count: 0, conversion: "100%" },
    { stage: "Screened",    count: 0, conversion: "0%"   },
    { stage: "Interviewed", count: 0, conversion: "0%"   },
    { stage: "Offered",     count: 0, conversion: "0%"   },
    { stage: "Placed",      count: 0, conversion: "0%"   },
  ],
  recruiters: [],
  clients:    [],
  sources:    [],
};

// ── Main ──────────────────────────────────────────────────────────────────────
export default function Reports() {
  // Own canvas + page padding only on the standalone /reports route; inside the
  // HR dashboard tab the dashboard supplies them.
  const { pathname } = useLocation();
  const standalone = pathname.toLowerCase().startsWith("/reports");

  const [tab,     setTab]     = useState(0);
  const [period,  setPeriod]  = useState("thisMonth");
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState("");
  const [data,    setData]    = useState(FALLBACK);

  const load = useCallback(async () => {
    setLoading(true); setError("");
    const endpoints = ["overview", "funnel", "recruiter-performance", "client-wise", "source-effectiveness"];
    const results = await Promise.allSettled(endpoints.map(ep => fetchReport(ep, period)));
    const val = (i) => (results[i].status === "fulfilled" ? results[i].value?.data : undefined);
    const ov = val(0);
    const fn = arr(val(1));
    setData({
      overview:   ov && typeof ov === "object" ? { ...FALLBACK.overview, ...ov } : FALLBACK.overview,
      funnel:     fn.length ? fn : FALLBACK.funnel,
      recruiters: arr(val(2)),
      clients:    arr(val(3)),
      sources:    arr(val(4)),
    });
    const failed = results.filter(r => r.status === "rejected");
    if (failed.length) {
      setError(failed.length === results.length
        ? (failed[0].reason?.message || "Failed to load report data")
        : `Some report sections could not be loaded: ${failed[0].reason?.message || "request failed"}`);
    }
    setLoading(false);
  }, [period]);

  useEffect(() => { load(); }, [load]);

  const ov = data.overview;

  // ── Chart data ──────────────────────────────────────────────────────────────
  const funnelChart = {
    labels: data.funnel.map(f => f.stage),
    datasets: [{
      data: data.funnel.map(f => num(f.count)),
      backgroundColor: SOFT,
      borderColor: COLORS,
      borderWidth: 1,
      borderRadius: 5,
    }],
  };

  const recruiterChart = {
    labels: data.recruiters.map(r => String(r.name || "—").split(" ")[0]),
    datasets: [
      { label: "Interviews", data: data.recruiters.map(r => num(r.interviews)),  backgroundColor: SOFT[0], borderRadius: 4 },
      { label: "Offers",     data: data.recruiters.map(r => num(r.offers)),      backgroundColor: SOFT[2], borderRadius: 4 },
      { label: "Placements", data: data.recruiters.map(r => num(r.placements)),  backgroundColor: SOFT[3], borderRadius: 4 },
    ],
  };

  const clientRevenueChart = {
    labels: data.clients.map(c => c.name || "—"),
    datasets: [{
      data: data.clients.map(c => num(c.revenue)),
      backgroundColor: SOFT,
      borderWidth: 0,
    }],
  };

  const clientFillChart = {
    labels: data.clients.map(c => c.name || "—"),
    datasets: [{
      data: data.clients.map(c => num(c.fill_rate)),
      backgroundColor: SOFT[0],
      borderRadius: 5,
    }],
  };

  const sourceLineChart = {
    labels: data.sources.map(s => s.source || "—"),
    datasets: [
      {
        label: "Candidates",
        data: data.sources.map(s => num(s.candidates)),
        borderColor: COLORS[0], backgroundColor: COLORS[0] + "18",
        fill: true, tension: 0.4, pointRadius: 5,
      },
      {
        label: "Hires",
        data: data.sources.map(s => num(s.hires)),
        borderColor: COLORS[3], backgroundColor: COLORS[3] + "18",
        fill: true, tension: 0.4, pointRadius: 5,
      },
    ],
  };

  const TABS = [
    { label: "Overview",              icon: <Dashboard fontSize="small" /> },
    { label: "Recruiter Performance", icon: <People    fontSize="small" /> },
    { label: "Client Analysis",       icon: <Business  fontSize="small" /> },
    { label: "Source Effectiveness",  icon: <Source    fontSize="small" /> },
  ];

  // Export the active tab as CSV.
  const handleExport = () => {
    let headers; let rows; let name;
    if (tab === 1) {
      name = "recruiter-performance";
      headers = ["Recruiter", "Jobs Posted", "Interviews", "Offers", "Placements", "Revenue", "Conversion %"];
      rows = data.recruiters.map(r => [r.name, num(r.jobs_posted), num(r.interviews), num(r.offers), num(r.placements), num(r.revenue), num(r.conversion_rate)]);
    } else if (tab === 2) {
      name = "client-analysis";
      headers = ["Client", "Active Jobs", "Pipeline", "Placements", "Revenue", "Fill Rate %"];
      rows = data.clients.map(c => [c.name, num(c.jobs), num(c.active_pipeline), num(c.placements), num(c.revenue), num(c.fill_rate)]);
    } else if (tab === 3) {
      name = "source-effectiveness";
      headers = ["Source", "Candidates", "Shortlisted", "Interviewed", "Hires", "Efficiency %"];
      rows = data.sources.map(s => [s.source, num(s.candidates), num(s.shortlisted), num(s.interviewed), num(s.hires), num(s.efficiency)]);
    } else {
      name = "overview";
      headers = ["Metric", "Value"];
      rows = [
        ["Total Jobs", num(ov.total_jobs)], ["Open Jobs", num(ov.open_jobs)],
        ["Candidates", num(ov.total_candidates)], ["Placements", num(ov.total_placements)],
        ["Fill Rate %", num(ov.fill_rate)], ["Avg Days to Fill", num(ov.avg_time_to_fill)],
        ["Revenue", num(ov.revenue)],
        ...data.funnel.map(f => [`Funnel: ${f.stage}`, num(f.count)]),
      ];
    }
    downloadCsv(`report-${name}-${period}.csv`, toCsv(headers, rows));
  };

  const pctBar = (value, color, width) => (
    <LinearProgress variant="determinate" value={Math.min(Math.max(num(value), 0), 100)}
      sx={{ width, flex: width ? "none" : 1, height: 6, borderRadius: 3, bgcolor: THEME.border,
        "& .MuiLinearProgress-bar": { bgcolor: color, borderRadius: 3 } }} />
  );

  const avatarCell = (label, i, text) => (
    <Box display="flex" alignItems="center" gap={1.5} minWidth={0}>
      <Avatar sx={{ width: 32, height: 32, fontSize: TYPE.overline, fontWeight: 700, bgcolor: COLORS[i % COLORS.length] }}>
        {text}
      </Avatar>
      <Typography title={label} sx={{ fontWeight: 600, fontSize: TYPE.small, maxWidth: 200, ...ELLIPSIS }}>{label || "—"}</Typography>
    </Box>
  );

  const body = (
    <Box display="flex" flexDirection="column" gap={3}>

      {/* ── Header ── */}
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2}>
        <Box>
          <Typography sx={{ fontFamily: FONT_DISPLAY, fontWeight: 700, fontSize: TYPE.page, lineHeight: 1.25, letterSpacing: "-0.02em", color: THEME.primaryDeep }}>
            Reports &amp; Analytics
          </Typography>
          <Typography sx={{ fontSize: TYPE.body, color: THEME.muted, mt: 0.5 }}>
            Comprehensive recruitment insights and performance metrics
          </Typography>
        </Box>
        <Box display="flex" gap={1.5} alignItems="center">
          <TextField select value={period} onChange={e => setPeriod(e.target.value)}
            size="small" disabled={loading}
            sx={{ minWidth: 160, "& .MuiOutlinedInput-root": { height: CONTROL_HEIGHT.md, bgcolor: THEME.surface } }}>
            <MenuItem value="thisWeek">This Week</MenuItem>
            <MenuItem value="thisMonth">This Month</MenuItem>
            <MenuItem value="lastMonth">Last Month</MenuItem>
            <MenuItem value="thisQuarter">This Quarter</MenuItem>
            <MenuItem value="thisYear">This Year</MenuItem>
          </TextField>
          <Tooltip title="Refresh">
            <span>
              <IconButton onClick={load} size="small" disabled={loading} aria-label="Refresh"
                sx={{ width: CONTROL_HEIGHT.md, height: CONTROL_HEIGHT.md, border: `1px solid ${THEME.border}`, borderRadius: `${RADIUS.sm}px`, bgcolor: THEME.surface }}>
                <Refresh fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Button variant="outlined" startIcon={<Download />} onClick={handleExport} disabled={loading}
            sx={{ height: CONTROL_HEIGHT.md, textTransform: "none", fontWeight: 600, borderRadius: `${RADIUS.sm}px`, bgcolor: THEME.surface }}>
            Export CSV
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="warning" onClose={() => setError("")}
          action={<Button color="inherit" size="small" onClick={load} sx={{ textTransform: "none", fontWeight: 600 }}>Retry</Button>}>
          {error}
        </Alert>
      )}

      {/* ── Tabs ── */}
      <Card elevation={0} sx={cardSx}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}
          sx={{ px: 2 }}
          variant="scrollable" scrollButtons="auto">
          {TABS.map((t, i) => (
            <Tab key={i} icon={t.icon} iconPosition="start" label={t.label} />
          ))}
        </Tabs>
      </Card>

      {loading ? (
        <Card elevation={0} sx={cardSx}>
          <Box display="flex" flexDirection="column" alignItems="center" py={10} gap={1.5}>
            <CircularProgress size={32} />
            <Typography sx={{ fontSize: TYPE.small, color: THEME.muted }}>Loading report…</Typography>
          </Box>
        </Card>
      ) : (
        <>
          {/* ══════════════════════════════════════════════════════════
              TAB 0 — OVERVIEW
             ══════════════════════════════════════════════════════════ */}
          {tab === 0 && (
            <Box display="flex" flexDirection="column" gap={3}>

              {/* KPI row */}
              <Grid container spacing={2}>
                <Grid item xs={6} md={3}>
                  <KpiCard title="Total Jobs"    value={num(ov.total_jobs)}
                    icon={<WorkOutline />}      color="#1a237e"
                    sub={`${num(ov.open_jobs)} open`} />
                </Grid>
                <Grid item xs={6} md={3}>
                  <KpiCard title="Placements"    value={num(ov.total_placements)}
                    icon={<CheckCircle />}      color="#2e7d32"
                    sub={`Fill rate ${num(ov.fill_rate)}%`} />
                </Grid>
                <Grid item xs={6} md={3}>
                  <KpiCard title="Candidates"    value={num(ov.total_candidates)}
                    icon={<People />}           color="#0277bd"
                    sub="In the pipeline" />
                </Grid>
                <Grid item xs={6} md={3}>
                  <KpiCard title="Avg Time to Fill" value={`${num(ov.avg_time_to_fill)} days`}
                    icon={<AccessTime />}       color="#e65100"
                    sub="Across all positions" />
                </Grid>
              </Grid>

              {/* Charts row */}
              <Grid container spacing={2}>
                <Grid item xs={12} md={7}>
                  <ChartCard title="Recruitment Funnel" height={300}>
                    <Bar data={funnelChart} options={BAR_OPTS} />
                  </ChartCard>
                </Grid>
                <Grid item xs={12} md={5}>
                  <Card elevation={0} sx={{ ...cardSx, height: "100%" }}>
                    <CardHeader title={<CardTitle>Pipeline Summary</CardTitle>} sx={cardHeaderSx} />
                    <CardContent sx={{ p: 3 }}>
                      {data.funnel.map((f, i) => (
                        <Box key={i} mb={2}>
                          <Box display="flex" justifyContent="space-between" mb={0.5}>
                            <Typography sx={{ fontSize: TYPE.small, fontWeight: 600 }}>{f.stage}</Typography>
                            <Box display="flex" gap={1} alignItems="center">
                              <Typography sx={{ ...numCellSx, fontWeight: 700 }}>{num(f.count)}</Typography>
                              <Chip label={f.conversion ?? "—"} size="small"
                                sx={{ height: 20, bgcolor: THEME.accentSoft, color: THEME.primaryDark }} />
                            </Box>
                          </Box>
                          {pctBar(num(data.funnel[0]?.count) ? (num(f.count) / num(data.funnel[0].count)) * 100 : 0, COLORS[i % COLORS.length])}
                        </Box>
                      ))}
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Revenue + Fill Rate KPIs */}
              <Grid container spacing={2}>
                {[
                  { label: "Total Revenue",    value: fmt(ov.revenue),           sub: "This period",       bg: "#1a237e" },
                  { label: "Fill Rate",        value: `${num(ov.fill_rate)}%`,   bar: num(ov.fill_rate),   bg: "#2e7d32" },
                  { label: "Avg Days to Fill", value: num(ov.avg_time_to_fill),  sub: "Days per position", bg: "#e65100" },
                ].map(k => (
                  <Grid item xs={12} md={4} key={k.label}>
                    <Card elevation={0} sx={{ bgcolor: k.bg, color: "#fff", height: "100%", borderRadius: `${RADIUS.md}px`, boxShadow: SHADOW.card }}>
                      <CardContent sx={{ p: 3 }}>
                        <Typography sx={{ fontSize: TYPE.overline, fontWeight: 700, opacity: 0.75, textTransform: "uppercase", letterSpacing: "0.08em" }}>{k.label}</Typography>
                        <Typography sx={{ ...statSx, fontSize: TYPE.stat, mt: 1 }}>{k.value}</Typography>
                        {k.bar != null ? (
                          <LinearProgress variant="determinate" value={Math.min(Math.max(k.bar, 0), 100)}
                            sx={{ mt: 2, bgcolor: "rgba(255,255,255,0.2)", "& .MuiLinearProgress-bar": { bgcolor: "#fff" }, height: 6, borderRadius: 3 }} />
                        ) : (
                          <Typography sx={{ fontSize: TYPE.caption, opacity: 0.75, mt: 1 }}>{k.sub}</Typography>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}

          {/* ══════════════════════════════════════════════════════════
              TAB 1 — RECRUITER PERFORMANCE
             ══════════════════════════════════════════════════════════ */}
          {tab === 1 && (
            <Box display="flex" flexDirection="column" gap={3}>
              {data.recruiters.length === 0 ? (
                <EmptyCard icon={<People />} title="No recruiter data yet"
                  sub="Data will appear once placements and tracking records are added" />
              ) : (
                <>
                  <ChartCard title="Recruiter Activity Comparison" height={320}>
                    <Bar data={recruiterChart}
                      options={{ ...BAR_OPTS, plugins: { legend: { display: true, position: "top" } } }} />
                  </ChartCard>

                  <TableCard title="Performance Breakdown">
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: THEME.surfaceTint }}>
                          {["Recruiter","Jobs Posted","Interviews","Offers","Placements","Revenue","Conversion"].map(h => (
                            <TableCell key={h} sx={headCellSx}>{h}</TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {data.recruiters.map((r, i) => {
                          const conv = num(r.conversion_rate);
                          return (
                            <TableRow key={i} hover>
                              <TableCell>{avatarCell(r.name, i, initialsOf(r.name))}</TableCell>
                              <TableCell sx={numCellSx}>{num(r.jobs_posted)}</TableCell>
                              <TableCell sx={numCellSx}>{num(r.interviews)}</TableCell>
                              <TableCell sx={numCellSx}>{num(r.offers)}</TableCell>
                              <TableCell>
                                <Chip label={num(r.placements)} size="small" color="success" sx={{ fontWeight: 700 }} />
                              </TableCell>
                              <TableCell sx={numCellSx}>{fmt(r.revenue)}</TableCell>
                              <TableCell>
                                <Box display="flex" alignItems="center" gap={1} width={120}>
                                  {pctBar(Math.min(conv, 100), conv >= 30 ? "#2e7d32" : conv >= 15 ? "#0277bd" : "#e65100")}
                                  <Typography sx={{ ...numCellSx, fontSize: TYPE.caption, fontWeight: 700, color: THEME.muted }}>{conv}%</Typography>
                                </Box>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableCard>
                </>
              )}
            </Box>
          )}

          {/* ══════════════════════════════════════════════════════════
              TAB 2 — CLIENT ANALYSIS
             ══════════════════════════════════════════════════════════ */}
          {tab === 2 && (
            <Box display="flex" flexDirection="column" gap={3}>
              {data.clients.length === 0 ? (
                <EmptyCard icon={<Business />} title="No client data yet"
                  sub="Data will appear once jobs and placements are linked to clients" />
              ) : (
                <>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={5}>
                      <ChartCard title="Revenue by Client" height={280}>
                        <Doughnut data={clientRevenueChart} options={DONUT_OPTS} />
                      </ChartCard>
                    </Grid>
                    <Grid item xs={12} md={7}>
                      <ChartCard title="Client Fill Rates (%)" height={280}>
                        <Bar data={clientFillChart}
                          options={{ ...BAR_OPTS, scales: { ...BAR_OPTS.scales, y: { ...BAR_OPTS.scales.y, max: 100 } } }} />
                      </ChartCard>
                    </Grid>
                  </Grid>

                  <TableCard title="Client Summary">
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: THEME.surfaceTint }}>
                          {["Client","Active Jobs","Pipeline","Placements","Revenue","Fill Rate"].map(h => (
                            <TableCell key={h} sx={headCellSx}>{h}</TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {data.clients.map((c, i) => {
                          const fill = num(c.fill_rate);
                          return (
                            <TableRow key={i} hover>
                              <TableCell>{avatarCell(c.name, i, String(c.name || "?").slice(0, 2).toUpperCase())}</TableCell>
                              <TableCell sx={numCellSx}>{num(c.jobs)}</TableCell>
                              <TableCell sx={numCellSx}>{num(c.active_pipeline)}</TableCell>
                              <TableCell>
                                <Chip label={num(c.placements)} size="small" color="primary" sx={{ fontWeight: 700 }} />
                              </TableCell>
                              <TableCell sx={{ ...numCellSx, fontWeight: 600 }}>{fmt(c.revenue)}</TableCell>
                              <TableCell>
                                <Box display="flex" alignItems="center" gap={1}>
                                  {pctBar(fill, fill >= 60 ? "#2e7d32" : fill >= 30 ? "#e65100" : "#c62828", 64)}
                                  <Chip label={`${fill}%`} size="small"
                                    color={fill >= 60 ? "success" : fill >= 30 ? "warning" : "error"}
                                    sx={{ fontWeight: 700 }} />
                                </Box>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableCard>
                </>
              )}
            </Box>
          )}

          {/* ══════════════════════════════════════════════════════════
              TAB 3 — SOURCE EFFECTIVENESS
             ══════════════════════════════════════════════════════════ */}
          {tab === 3 && (
            <Box display="flex" flexDirection="column" gap={3}>
              {data.sources.length === 0 ? (
                <EmptyCard icon={<Source />} title="No source data yet"
                  sub="Data will appear once candidates have source values assigned" />
              ) : (
                <>
                  <ChartCard title="Source Performance — Candidates vs Hires" height={300}>
                    <Line data={sourceLineChart}
                      options={{ ...BAR_OPTS, plugins: { legend: { display: true, position: "top" } } }} />
                  </ChartCard>

                  {/* Insight cards */}
                  {(() => {
                    const best      = [...data.sources].sort((a, b) => num(b.efficiency) - num(a.efficiency))[0];
                    const volume    = [...data.sources].sort((a, b) => num(b.candidates) - num(a.candidates))[0];
                    const mostHires = [...data.sources].sort((a, b) => num(b.hires) - num(a.hires))[0];
                    return (
                      <Grid container spacing={2}>
                        {[
                          { label: "Best Efficiency", value: best?.source,      stat: `${num(best?.efficiency)}%`,             color: "#2e7d32", icon: <TrendingUp /> },
                          { label: "Volume Leader",   value: volume?.source,    stat: `${num(volume?.candidates)} candidates`, color: "#0277bd", icon: <People /> },
                          { label: "Most Hires",      value: mostHires?.source, stat: `${num(mostHires?.hires)} hires`,         color: "#e65100", icon: <CheckCircle /> },
                        ].map((ins, i) => (
                          <Grid item xs={12} md={4} key={i}>
                            <Card elevation={0} sx={{ ...cardSx, borderRadius: `${RADIUS.md}px`, height: "100%" }}>
                              <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                                <Box display="flex" alignItems="center" gap={1.5} minWidth={0}>
                                  <Avatar sx={{ bgcolor: `${ins.color}18`, color: ins.color }}>{ins.icon}</Avatar>
                                  <Box minWidth={0}>
                                    <Typography sx={{ fontSize: TYPE.overline, fontWeight: 700, color: THEME.muted, textTransform: "uppercase", letterSpacing: "0.08em" }}>{ins.label}</Typography>
                                    <Typography title={ins.value} sx={{ fontWeight: 700, fontSize: TYPE.cardTitle, ...ELLIPSIS }}>{ins.value || "—"}</Typography>
                                    <Typography sx={{ fontSize: TYPE.caption, color: THEME.muted }}>{ins.stat}</Typography>
                                  </Box>
                                </Box>
                              </CardContent>
                            </Card>
                          </Grid>
                        ))}
                      </Grid>
                    );
                  })()}

                  <TableCard title="Source Breakdown">
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: THEME.surfaceTint }}>
                          {["Source","Candidates","Shortlisted","Interviewed","Hires","Efficiency"].map(h => (
                            <TableCell key={h} sx={headCellSx}>{h}</TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {data.sources.map((s, i) => {
                          const eff = num(s.efficiency);
                          return (
                            <TableRow key={i} hover>
                              <TableCell>
                                <Chip label={s.source || "—"} size="small"
                                  sx={{ fontWeight: 700, bgcolor: COLORS[i % COLORS.length] + "18", color: COLORS[i % COLORS.length] }} />
                              </TableCell>
                              <TableCell sx={numCellSx}>{num(s.candidates)}</TableCell>
                              <TableCell sx={numCellSx}>{num(s.shortlisted)}</TableCell>
                              <TableCell sx={numCellSx}>{num(s.interviewed)}</TableCell>
                              <TableCell>
                                <Chip label={num(s.hires)} size="small" color="success" sx={{ fontWeight: 700 }} />
                              </TableCell>
                              <TableCell>
                                <Box display="flex" alignItems="center" gap={1}>
                                  {pctBar(eff, eff >= 80 ? "#2e7d32" : eff >= 50 ? "#0277bd" : "#e65100", 72)}
                                  <Typography sx={{ ...numCellSx, fontSize: TYPE.caption, fontWeight: 700, color: THEME.muted }}>{eff}%</Typography>
                                </Box>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableCard>
                </>
              )}
            </Box>
          )}
        </>
      )}
    </Box>
  );

  return (
    <ThemeProvider theme={recruiterTheme}>
      {standalone ? (
        <Box sx={{ bgcolor: THEME.background, minHeight: "100vh", p: `${PAGE_PADDING}px`, fontFamily: FONT_BODY }}>
          {body}
        </Box>
      ) : body}
    </ThemeProvider>
  );
}
